// Meta Pixel loader, gated on marketing consent.
//
// We deliberately wait for explicit consent before injecting the Pixel
// script tag — the bare network request to connect.facebook.net itself
// transfers IP + UA to Meta, which counts as personal data under GDPR
// + Romanian Law 506/2004. So consent gates the *script load*, not just
// the cookies it sets.
//
// Pixel events fired from this app:
//   PageView         — automatic on init
//   InitiateCheckout — fired explicitly from Payment.jsx with a generated
//                      event_id; the same id is plumbed through to the
//                      server-side Conversions API call so Meta dedupes
//                      browser + server events.
//   Purchase         — fired ONLY server-side via Conversions API. Browser
//                      doesn't know the order until the customer returns
//                      from Stripe Checkout, by which point CAPI has
//                      already reported it.
//
// Revoke flow: when the user toggles marketing off via /cookies, we call
// fbq('consent', 'revoke') AND reload the page. Pixel SDK doesn't fully
// detach without a reload, so the cleanest UX is the reload pattern.

import { hasMarketingConsent, subscribe } from './consent.js';

const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID;

let initialised = false;

function injectFbq() {
  if (initialised) return;
  if (typeof window === 'undefined') return;
  initialised = true;

  // Standard Meta Pixel base snippet, transcribed from Meta's official
  // installer. Verbatim apart from formatting and the explicit consent
  // grant call right after init.
  /* eslint-disable */
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq('consent', 'grant');
  window.fbq('init', PIXEL_ID);
  window.fbq('track', 'PageView');
}

/**
 * Fire a Meta Pixel `track` event. Safe to call before/after init —
 * Meta's queue absorbs early calls. The eventId is the dedup key against
 * the server-side Conversions API call that Stripe webhook will fire
 * after payment success.
 *
 * @param {string} eventName - Standard Pixel event name (InitiateCheckout, etc.)
 * @param {Record<string, unknown>=} params - Optional event parameters
 * @param {string=} eventId - Dedup id shared with CAPI
 */
export function trackPixelEvent(eventName, params, eventId) {
  if (typeof window === 'undefined') return;
  if (!PIXEL_ID) return;
  if (!hasMarketingConsent()) return;
  if (!initialised) injectFbq();
  if (typeof window.fbq !== 'function') return;
  const opts = eventId ? { eventID: eventId } : undefined;
  if (params && opts) {
    window.fbq('track', eventName, params, opts);
  } else if (params) {
    window.fbq('track', eventName, params);
  } else if (opts) {
    window.fbq('track', eventName, {}, opts);
  } else {
    window.fbq('track', eventName);
  }
}

if (typeof window !== 'undefined') {
  if (!PIXEL_ID && import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn('[crayo] VITE_META_PIXEL_ID unset; Meta Pixel disabled.');
  }

  if (PIXEL_ID && hasMarketingConsent()) {
    // User has previously accepted on this device; init immediately.
    injectFbq();
  }

  // React to runtime consent changes. Grant -> reload (banner already
  // forces a reload on grant, this is the belt). Revoke -> revoke +
  // reload, since the SDK can't fully detach without a reload.
  subscribe((state) => {
    const granted = state?.marketing === true;
    if (granted && !initialised && PIXEL_ID) {
      injectFbq();
    } else if (!granted && initialised && typeof window.fbq === 'function') {
      try {
        window.fbq('consent', 'revoke');
      } catch {
        // ignore; reload below cleans up regardless
      }
      window.location.reload();
    }
  });

  // Expose for legacy components that hop through window globals.
  window.crayoPixel = { trackPixelEvent };
}
